import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface TourStep {
  targetId: string;
  title: string;
  text: string;
}

const STORAGE_KEY = 'clinique_medecin_tour_count';
const MAX_SHOWS = 3;

function getTourCount(): number {
  try {
    const value = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

const steps: TourStep[] = [
  {
    targetId: 'guide-btn-nouvelle-consultation',
    title: 'Nouvelle consultation',
    text: 'Démarrez une consultation immédiate (sans rendez-vous) en recherchant un patient par nom, prénom ou n° de dossier.',
  },
  {
    targetId: 'guide-btn-file-attente',
    title: 'File d\'attente',
    text: 'Consultez la file d\'attente de votre salle en temps réel pour savoir qui est le prochain patient.',
  },
  {
    targetId: 'guide-btn-agenda',
    title: 'Agenda',
    text: 'Ouvrez l\'agenda pour visualiser tous vos rendez-vous, créer un créneau ou consulter les jours passés.',
  },
  {
    targetId: 'guide-list-rdv',
    title: 'Rendez-vous à venir',
    text: 'Pour chaque rendez-vous « En attente », cliquez sur « Démarrer » pour lancer la consultation avec ce patient.',
  },
  {
    targetId: 'guide-patients-clinique',
    title: 'Patients de la clinique',
    text: 'Recherchez n\'importe quel patient de la clinique et cliquez sur son nom pour ouvrir son dossier médical.',
  },
];

export default function MedecinTour() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lance la visite lors des 3 premières connexions uniquement
  useEffect(() => {
    if (getTourCount() < MAX_SHOWS) {
      timerRef.current = setTimeout(() => setOpen(true), 600);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const positionSpotlight = useCallback(() => {
    const s = steps[step];
    const el = document.getElementById(s.targetId);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Laisser le scroll se stabiliser avant de mesurer
    setTimeout(() => {
      const el2 = document.getElementById(s.targetId);
      if (el2) setRect(el2.getBoundingClientRect());
    }, 400);
  }, [step]);

  useEffect(() => {
    if (!open) return;
    positionSpotlight();
  }, [open, step, positionSpotlight]);

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(getTourCount() + 1));
    } catch {
      // silencieux
    }
    setOpen(false);
    setRect(null);
  };

  const next = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      finish();
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const goToGuide = () => {
    finish();
    navigate('/medecin/guide');
  };

  if (!open) return null;

  const current = steps[step];

  return (
    <div className="fixed inset-0 z-[300]">
      {/* Overlay avec trou (spotlight) */}
      {rect ? (
        <div
          className="absolute transition-all duration-300"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 16,
            boxShadow: '0 0 0 9999px rgba(8, 32, 48, 0.6)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/60" />
      )}

      {/* Carte d'explication (en bas, centrée) */}
      <div className="absolute inset-x-0 bottom-6 flex justify-center px-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 md:p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-teal-600 text-white rounded-full flex items-center justify-center shrink-0">
                <i className="ri-compass-3-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-teal-600 uppercase tracking-wide">
                  Visite guidée · {step + 1}/{steps.length}
                </p>
                <h3 className="text-base md:text-lg font-bold text-gray-900">{current.title}</h3>
              </div>
            </div>
            <button
              onClick={finish}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center cursor-pointer shrink-0"
              aria-label="Fermer la visite guidée"
            >
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>

          <p className="text-sm text-gray-600 leading-relaxed mb-5">{current.text}</p>

          {/* Points de progression */}
          <div className="flex items-center gap-1.5 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-teal-600' : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={goToGuide}
              className="text-xs md:text-sm text-gray-500 hover:text-teal-600 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line mr-1"></i>
              Guide complet
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  onClick={prev}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
                >
                  Précédent
                </button>
              )}
              {step < steps.length - 1 && (
                <button
                  onClick={() => finish()}
                  className="px-4 py-2 text-gray-400 hover:text-gray-600 font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  Passer
                </button>
              )}
              <button
                onClick={next}
                className="px-5 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 font-semibold text-sm whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5"
              >
                {step < steps.length - 1 ? 'Suivant' : 'Terminer'}
                {step < steps.length - 1 && <i className="ri-arrow-right-line"></i>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}